/****************
 * COPYFI LITE AND COPYFI PRO. Guillaume, 2026-10-22.
 *
 * TWIN FILE: frontend/src/lib/uiMode.core.ts and mobile-app/src/lib/uiMode.core.ts
 * must stay byte-identical.
 *
 * "Divide the UI in two, CopyFi Lite and CopyFi Pro ... both free and
 * accessible by everybody ... a new user ... a light version would speed run
 * him faster from Log In to launch backtest and paper session and he would
 * encounter less data and less options." And: "Pro is technically everything
 * we already have now, light is just a version where everything not-essential
 * is removed." One app, two modes, on web and phone alike, with a Switch to Pro /
 * Switch to Lite button.
 *
 * Lite HIDES, it never locks: a Pro page opened by a link still opens. Adding a
 * broker key and going live stay in Lite (Guillaume 10-22).
 *
 * The choice lives on the account (Clerk unsafeMetadata.uiMode) so web and
 * phone agree; this file is only the rules.
 ****************/

export type UiMode = 'lite' | 'pro';

/** Accounts created from this moment start in Lite; older ones keep Pro, so
 *  nobody loses a screen they use. Set to the release day of the switch. */
export const LITE_FOR_ACCOUNTS_FROM = '2026-10-23T00:00:00Z';

export function isUiMode(v: unknown): v is UiMode {
  return v === 'lite' || v === 'pro';
}

/** The mode an account sees: its own choice, else Lite for a new account, Pro for an older one. */
export function resolveUiMode(chosen: unknown, createdAt: Date | string | number | null | undefined): UiMode {
  if (isUiMode(chosen)) return chosen;
  const created = createdAt ? new Date(createdAt).getTime() : NaN;
  if (!Number.isFinite(created)) return 'pro';
  return created >= Date.parse(LITE_FOR_ACCOUNTS_FROM) ? 'lite' : 'pro';
}

/**
 * What Lite leaves out. Menu places by their route, and the Dashboard's
 * sections by id. Everything not listed is in both modes.
 */
export const PRO_ONLY_PLACES = ['/assets', '/market', '/ai', '/community'] as const;
export const PRO_ONLY_DASHBOARD_SECTIONS = ['dash-market', 'dash-leaderboard', 'dash-trades'] as const;

export function placeInMode(route: string, mode: UiMode): boolean {
  if (mode === 'pro') return true;
  const path = route.replace(/^\/\(tabs\)/, '') || '/';
  return !PRO_ONLY_PLACES.some((p) => path === p || path.startsWith(`${p}/`) || path.startsWith(`${p}?`));
}

export function dashboardSectionInMode(id: string, mode: UiMode): boolean {
  return mode === 'pro' || !(PRO_ONLY_DASHBOARD_SECTIONS as readonly string[]).includes(id);
}

/** Lite's starting amount for the first backtest and practice session (Guillaume
 *  2026-10-22: "light version and speedrun toward first backtest and paper session
 *  for new users can have some" defaults). Pro starts empty. Always editable. */
export const LITE_START_AMOUNT = 1000;
